import { AccountType } from "@common/constants/domains/accounting/accounts";
import { DraftGroup, DraftState, GenerateId } from "./draftState";
import {
    nextSortOrderInScope,
    placeNewRowInDraftScope,
    ScopeInsertPosition,
} from "./draftSortOrder";

// Normalised key for case-insensitive, trimmed group-name comparison. Mirrors
// the backend's citext uniqueness so the editor never builds a draft the server
// would reject.
export const normalizeGroupName = (name: string): string =>
    name.trim().toLowerCase();

interface GroupScope {
    groups: DraftGroup[];
    // Creating a missing group in business B never returns business A's group id.
    entityId: number;
    type: AccountType;
    parentGroupId: string | null;
}

export const collectSubtreeGroupIds = (
    rootId: string,
    groups: DraftGroup[],
): Set<string> => {
    const subtreeIds = new Set<string>([rootId]);
    let didAdd = true;
    while (didAdd) {
        didAdd = false;
        for (const group of groups) {
            if (
                group.parentGroupId != null &&
                subtreeIds.has(group.parentGroupId) &&
                !subtreeIds.has(group.id)
            ) {
                subtreeIds.add(group.id);
                didAdd = true;
            }
        }
    }
    return subtreeIds;
};

// Report-leaf folding is presentation-only; group options use exact type and parent scope.
const getGroupsInScope = ({
    groups,
    entityId,
    type,
    parentGroupId,
}: GroupScope): DraftGroup[] =>
    groups.filter(
        (group) =>
            group.entityId === entityId &&
            group.type === type &&
            group.parentGroupId === parentGroupId,
    );

// Returns the existing group in scope whose name case-insensitively (trimmed)
// matches `name`, or undefined when the name is free to use.
export const findGroupInScope = ({
    groups,
    entityId,
    type,
    parentGroupId,
    name,
}: GroupScope & { name: string }): DraftGroup | undefined => {
    const normalized = normalizeGroupName(name);
    return getGroupsInScope({ groups, entityId, type, parentGroupId }).find(
        (group) => normalizeGroupName(group.name) === normalized,
    );
};

interface CreateGroupParams {
    state: DraftState;
    entityId: number;
    type: AccountType;
    parentGroupId: string | null;
    name: string;
    position?: ScopeInsertPosition;
    generateId: GenerateId;
}

export const createGroup = ({
    state,
    entityId,
    type,
    parentGroupId,
    name,
    position = { kind: "append" },
    generateId,
}: CreateGroupParams): { state: DraftState; groupId: string } => {
    const existing = findGroupInScope({
        groups: state.groups,
        entityId,
        type,
        parentGroupId,
        name,
    });
    if (existing) {
        return { state, groupId: existing.id };
    }
    const groupId = generateId();

    if (position.kind === "append") {
        const newGroup: DraftGroup = {
            id: groupId,
            entityId,
            name,
            type,
            parentGroupId,
            sortOrder: nextSortOrderInScope({
                accounts: state.accounts,
                groups: state.groups,
                entityId,
                type,
                parentGroupId,
            }),
        };
        return {
            state: { ...state, groups: [...state.groups, newGroup] },
            groupId,
        };
    }

    const newGroup: DraftGroup = {
        id: groupId,
        entityId,
        name,
        type,
        parentGroupId,
        sortOrder: 0,
    };
    return {
        state: {
            ...state,
            ...placeNewRowInDraftScope({
                accounts: state.accounts,
                groups: state.groups,
                entityId,
                type,
                parentGroupId,
                position,
                placed: { id: groupId, kind: "group", name },
                nextAccounts: state.accounts,
                nextGroups: [...state.groups, newGroup],
            }),
        },
        groupId,
    };
};

interface AddGroupInScopeParams {
    state: DraftState;
    entityId: number;
    type: AccountType;
    parentGroupId: string | null;
    // Where the new group lands within its scope's group band; defaults to
    // appending at the bottom.
    position?: ScopeInsertPosition;
    generateId: GenerateId;
}

// Empty names must mint; createGroup would treat two blanks as the same name.
export const addGroupInScope = ({
    state,
    entityId,
    type,
    parentGroupId,
    position = { kind: "append" },
    generateId,
}: AddGroupInScopeParams): { state: DraftState; groupId: string } => {
    const groupId = generateId();
    const baseGroup: Omit<DraftGroup, "sortOrder"> = {
        id: groupId,
        entityId,
        name: "",
        type,
        parentGroupId,
    };

    if (position.kind === "append") {
        const newGroup: DraftGroup = {
            ...baseGroup,
            sortOrder: nextSortOrderInScope({
                accounts: state.accounts,
                groups: state.groups,
                entityId,
                type,
                parentGroupId,
            }),
        };
        return {
            state: { ...state, groups: [...state.groups, newGroup] },
            groupId,
        };
    }

    const newGroup: DraftGroup = {
        ...baseGroup,
        sortOrder: 0,
    };
    return {
        state: {
            ...state,
            ...placeNewRowInDraftScope({
                accounts: state.accounts,
                groups: state.groups,
                entityId,
                type,
                parentGroupId,
                position,
                placed: { id: groupId, kind: "group", name: "" },
                nextAccounts: state.accounts,
                nextGroups: [...state.groups, newGroup],
            }),
        },
        groupId,
    };
};

// Lift direct children into the deleted group's parent scope so the subtree remains connected.
export const deleteGroup = ({
    state,
    id,
}: {
    state: DraftState;
    id: string;
}): DraftState => {
    const target = state.groups.find((group) => group.id === id);
    if (!target) {
        return state;
    }
    return {
        ...state,
        accounts: state.accounts.map((account) =>
            account.parentGroupId === id
                ? { ...account, parentGroupId: target.parentGroupId }
                : account,
        ),
        groups: state.groups
            .filter((group) => group.id !== id)
            .map((group) =>
                group.parentGroupId === id
                    ? { ...group, parentGroupId: target.parentGroupId }
                    : group,
            ),
    };
};

interface RenameGroupParams {
    state: DraftState;
    id: string;
    name: string;
}

// Renames a custom group, mirroring updateAccount: the trimmed name is written
// into the draft as-is, even when blank or a case-insensitive duplicate of a
// sibling. The shared validation/issues layer flags those (blank → "Add name"
// affordance; duplicate → red error) and blocks Save, so the editor surfaces
// the problem inline instead of silently discarding the edit — matching how a
// blank account name / duplicate code is accepted into the draft.
export const renameGroup = ({
    state,
    id,
    name,
}: RenameGroupParams): DraftState => {
    const target = state.groups.find((group) => group.id === id);
    if (!target) {
        return state;
    }
    const trimmed = name.trim();
    return {
        ...state,
        groups: state.groups.map((group) =>
            group.id === id ? { ...group, name: trimmed } : group,
        ),
    };
};
